import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { projectService } from '../services/projectService';

/**
 * Project store using standardized API client
 */
export const useProjectStore = defineStore('projects', () => {
  // State
  const projects = ref([]);
  const currentProject = ref(null);
  const tasks = ref([]);
  const teamMembers = ref([]);
  const documents = ref([]);
  const milestones = ref([]);
  const loadingCount = ref(0);
  const isLoading = computed(() => loadingCount.value > 0);
  const error = ref(null);
  const filterStatus = ref('all');
  const filterHealth = ref('all');
  const filterType = ref('all');
  const searchQuery = ref('');
  const showArchived = ref(false);
  const pagination = ref({
    currentPage: 1,
    perPage: 20,
    totalItems: 0,
    totalPages: 0,
  });

  // Status and health color mappings
  const statusColors = {
    planning: 'grey-6',
    in_progress: 'blue',
    on_hold: 'orange',
    completed: 'positive',
    cancelled: 'negative',
    archived: 'grey-4',
  };

  const healthColors = {
    green: 'positive',
    yellow: 'warning',
    red: 'negative',
  };

  const priorityColors = {
    critical: 'negative',
    high: 'orange',
    medium: 'primary',
    low: 'grey-6',
  };

  // Getters
  const filteredProjects = computed(() => {
    return projects.value.filter((project) => {
      // Apply status filter
      if (filterStatus.value !== 'all' && project.status !== filterStatus.value) {
        return false;
      }

      // Apply health filter
      if (filterHealth.value !== 'all' && project.health_status !== filterHealth.value) {
        return false;
      }

      // Apply type filter
      if (filterType.value !== 'all' && project.project_type !== filterType.value) {
        return false;
      }

      // Apply archived filter
      if (!showArchived.value && project.status === 'archived') {
        return false;
      }

      // Apply search filter
      if (searchQuery.value) {
        const search = searchQuery.value.toLowerCase();
        return (
          project.name?.toLowerCase().includes(search) ||
          project.project_number?.toLowerCase().includes(search) ||
          project.description?.toLowerCase().includes(search)
        );
      }

      return true;
    });
  });

  const statusOptions = computed(() => [
    { label: 'All Statuses', value: 'all' },
    { label: 'Planning', value: 'planning' },
    { label: 'In Progress', value: 'in_progress' },
    { label: 'On Hold', value: 'on_hold' },
    { label: 'Completed', value: 'completed' },
    { label: 'Cancelled', value: 'cancelled' },
  ]);

  const healthOptions = computed(() => [
    { label: 'All Health', value: 'all' },
    { label: 'On Track', value: 'green' },
    { label: 'At Risk', value: 'yellow' },
    { label: 'Critical', value: 'red' },
  ]);

  const typeOptions = computed(() => [
    { label: 'All Types', value: 'all' },
    { label: 'Installation', value: 'installation' },
    { label: 'Maintenance', value: 'maintenance' },
    { label: 'Upgrade', value: 'upgrade' },
    { label: 'Retrofit', value: 'retrofit' },
    { label: 'Design', value: 'design' },
  ]);

  const projectStats = computed(() => {
    const stats = {
      total: projects.value.length,
      active: 0,
      planning: 0,
      in_progress: 0,
      on_hold: 0,
      completed: 0,
      green: 0,
      yellow: 0,
      red: 0,
      overdue: 0,
    };

    const now = new Date();

    projects.value.forEach((project) => {
      if (project.status !== 'archived' && project.status !== 'cancelled') {
        stats.active++;
      }

      // Status counts
      if (Object.hasOwn(stats, project.status)) {
        stats[project.status]++;
      }

      // Health counts
      if (Object.hasOwn(stats, project.health_status)) {
        stats[project.health_status]++;
      }

      // Overdue check
      if (project.planned_end_date && new Date(project.planned_end_date) < now &&
          project.status !== 'completed' && project.status !== 'cancelled') {
        stats.overdue++;
      }
    });

    return stats;
  });

  const getProjectById = computed(() => {
    return (id) => projects.value.find((project) => project.id === parseInt(id));
  });

  // ==================== PROJECT ACTIONS ====================

  /**
   * Fetch all projects with optional filtering and pagination
   * @param {Object} options - Fetch options
   * @returns {Promise<Array>} Fetched projects
   */
  const fetchProjects = async (options = {}) => {
    loadingCount.value++;
    error.value = null;

    try {

      // Build params for API call
      const params = { ...options };

      // Map status filter to API parameter
      if (filterStatus.value !== 'all') {
        params.status = filterStatus.value;
      }

      // Map health filter
      if (filterHealth.value !== 'all') {
        params.health_status = filterHealth.value;
      }

      // Map type filter
      if (filterType.value !== 'all') {
        params.project_type = filterType.value;
      }

      // Map search query to API parameter
      if (searchQuery.value) {
        params.q = searchQuery.value;
      }

      // Include archived filter
      if (showArchived.value) {
        params.include_archived = true;
      }

      // Use standardized service to fetch projects
      const result = await projectService.getProjects(params);

      // Extract projects and pagination info
      const fetchedProjects = result.data || [];

      // Update pagination if available
      if (result.pagination) {
        pagination.value = {
          currentPage: result.pagination.current_page,
          perPage: result.pagination.per_page,
          totalItems: result.pagination.total_items,
          totalPages: result.pagination.total_pages,
        };
      }


      projects.value = fetchedProjects;
      return fetchedProjects;
    } catch (err) {
      console.error('PROJECTS STORE: Error fetching projects:', err);
      error.value = err.message;
      throw err;
    } finally {
      loadingCount.value--;
    }
  };

  /**
   * Fetch a single project by ID
   * @param {number|string} id - Project ID
   * @param {Array|string} include - Related data to include
   * @returns {Promise<Object>} Fetched project
   */
  const fetchProject = async (id, include = ['tasks', 'team', 'documents', 'milestones']) => {
    loadingCount.value++;
    error.value = null;

    try {
      const project = await projectService.getProject(id, include);

      // Update current project
      currentProject.value = project;

      // Also update this project in the projects array if it exists
      const index = projects.value.findIndex((p) => p.id === parseInt(id));
      if (index !== -1) {
        projects.value[index] = project;
      } else {
        // Add to array if not present
        projects.value.push(project);
      }

      return project;
    } catch (err) {
      console.error(`PROJECTS STORE: Error fetching project ${id}:`, err);
      error.value = err.message;
      throw err;
    } finally {
      loadingCount.value--;
    }
  };

  /**
   * Create a new project
   * @param {Object} projectData - Project data
   * @returns {Promise<Object>} Created project
   */
  const createProject = async (projectData) => {
    loadingCount.value++;
    error.value = null;

    try {
      const response = await projectService.createProject(projectData);
      const newProject = response.project || response;

      // Add the new project to the beginning of the list
      projects.value.unshift(newProject);

      return newProject;
    } catch (err) {
      console.error('PROJECTS STORE: Error creating project:', err);
      error.value = err.message;
      throw err;
    } finally {
      loadingCount.value--;
    }
  };

  /**
   * Update an existing project
   * @param {number|string} id - Project ID
   * @param {Object} projectData - Updated project data
   * @returns {Promise<Object>} Updated project
   */
  const updateProject = async (id, projectData) => {
    loadingCount.value++;
    error.value = null;

    try {
      const updatedProject = await projectService.updateProject(id, projectData);

      // Update the projects array with the updated project
      const index = projects.value.findIndex((p) => p.id === parseInt(id));
      if (index !== -1) {
        projects.value[index] = updatedProject;
      }

      // Update current project if it's the same as the one being updated
      if (currentProject.value && currentProject.value.id === parseInt(id)) {
        currentProject.value = updatedProject;
      }

      return updatedProject;
    } catch (err) {
      console.error(`PROJECTS STORE: Error updating project ${id}:`, err);
      error.value = err.message;
      throw err;
    } finally {
      loadingCount.value--;
    }
  };

  /**
   * Archive a project
   * @param {number|string} id - Project ID
   * @returns {Promise<Object>} Archive result
   */
  const archiveProject = async (id) => {
    loadingCount.value++;
    error.value = null;

    try {
      const result = await projectService.archiveProject(id);

      // Update the project status in the array
      const index = projects.value.findIndex((p) => p.id === parseInt(id));
      if (index !== -1) {
        projects.value[index].status = 'archived';
      }

      // Reset current project if it's the one being archived
      if (currentProject.value && currentProject.value.id === parseInt(id)) {
        currentProject.value.status = 'archived';
      }

      return result;
    } catch (err) {
      console.error(`PROJECTS STORE: Error archiving project ${id}:`, err);
      error.value = err.message;
      throw err;
    } finally {
      loadingCount.value--;
    }
  };

  /**
   * Restore an archived project
   * @param {number|string} id - Project ID
   * @returns {Promise<Object>} Restored project
   */
  const restoreProject = async (id) => {
    loadingCount.value++;
    error.value = null;

    try {
      const restoredProject = await projectService.restoreProject(id);

      // Update the project in the array
      const index = projects.value.findIndex((p) => p.id === parseInt(id));
      if (index !== -1) {
        projects.value[index] = restoredProject;
      }

      // Update current project if it's the one being restored
      if (currentProject.value && currentProject.value.id === parseInt(id)) {
        currentProject.value = restoredProject;
      }

      return restoredProject;
    } catch (err) {
      console.error(`PROJECTS STORE: Error restoring project ${id}:`, err);
      error.value = err.message;
      throw err;
    } finally {
      loadingCount.value--;
    }
  };

  /**
   * Convert a tender to a project
   * @param {number|string} tenderId - Tender ID
   * @param {Object} conversionData - Conversion settings
   * @returns {Promise<Object>} Created project
   */
  const convertTenderToProject = async (tenderId, conversionData) => {
    loadingCount.value++;
    error.value = null;

    try {
      const response = await projectService.convertTenderToProject(tenderId, conversionData);
      // API returns { message, project } - extract the project
      const newProject = response.project || response;

      // Add the new project to the beginning of the list
      projects.value.unshift(newProject);

      return response;
    } catch (err) {
      console.error(`PROJECTS STORE: Error converting tender ${tenderId}:`, err);
      error.value = err.message;
      throw err;
    } finally {
      loadingCount.value--;
    }
  };

  // ==================== TASK ACTIONS ====================

  /**
   * Fetch tasks for a project
   * @param {number|string} projectId - Project ID
   * @returns {Promise<Array>} Fetched tasks
   */
  const fetchTasks = async (projectId) => {
    loadingCount.value++;
    error.value = null;

    try {
      const result = await projectService.getTasks(projectId);
      tasks.value = result.data || result;
      return tasks.value;
    } catch (err) {
      console.error(`PROJECTS STORE: Error fetching tasks for project ${projectId}:`, err);
      error.value = err.message;
      throw err;
    } finally {
      loadingCount.value--;
    }
  };

  /**
   * Create a new task
   * @param {number|string} projectId - Project ID
   * @param {Object} taskData - Task data
   * @returns {Promise<Object>} Created task
   */
  const createTask = async (projectId, taskData) => {
    loadingCount.value++;
    error.value = null;

    try {
      const newTask = await projectService.createTask(projectId, taskData);
      tasks.value.push(newTask);
      return newTask;
    } catch (err) {
      console.error('PROJECTS STORE: Error creating task:', err);
      error.value = err.message;
      throw err;
    } finally {
      loadingCount.value--;
    }
  };

  /**
   * Update a task
   * @param {number|string} projectId - Project ID
   * @param {number|string} taskId - Task ID
   * @param {Object} taskData - Updated task data
   * @returns {Promise<Object>} Updated task
   */
  const updateTask = async (projectId, taskId, taskData) => {
    loadingCount.value++;
    error.value = null;

    try {
      const response = await projectService.updateTask(projectId, taskId, taskData);
      const updatedTask = response.task || response;

      const index = tasks.value.findIndex((t) => t.id === parseInt(taskId));
      if (index !== -1) {
        tasks.value[index] = updatedTask;
      }

      return updatedTask;
    } catch (err) {
      console.error(`PROJECTS STORE: Error updating task ${taskId}:`, err);
      error.value = err.message;
      throw err;
    } finally {
      loadingCount.value--;
    }
  };

  /**
   * Delete a task
   * @param {number|string} projectId - Project ID
   * @param {number|string} taskId - Task ID
   * @returns {Promise<Object>} Deletion result
   */
  const deleteTask = async (projectId, taskId) => {
    loadingCount.value++;
    error.value = null;

    try {
      const result = await projectService.deleteTask(projectId, taskId);
      tasks.value = tasks.value.filter((t) => t.id !== parseInt(taskId));
      return result;
    } catch (err) {
      console.error(`PROJECTS STORE: Error deleting task ${taskId}:`, err);
      error.value = err.message;
      throw err;
    } finally {
      loadingCount.value--;
    }
  };

  /**
   * Mark a task as complete
   * @param {number|string} projectId - Project ID
   * @param {number|string} taskId - Task ID
   * @returns {Promise<Object>} Updated task
   */
  const completeTask = async (projectId, taskId) => {
    loadingCount.value++;
    error.value = null;

    try {
      const updatedTask = await projectService.completeTask(projectId, taskId);

      const index = tasks.value.findIndex((t) => t.id === parseInt(taskId));
      if (index !== -1) {
        tasks.value[index] = updatedTask;
      }

      return updatedTask;
    } catch (err) {
      console.error(`PROJECTS STORE: Error completing task ${taskId}:`, err);
      error.value = err.message;
      throw err;
    } finally {
      loadingCount.value--;
    }
  };

  // ==================== TEAM MEMBER ACTIONS ====================

  /**
   * Fetch team members for a project
   * @param {number|string} projectId - Project ID
   * @returns {Promise<Array>} Fetched team members
   */
  const fetchTeamMembers = async (projectId) => {
    loadingCount.value++;
    error.value = null;

    try {
      const result = await projectService.getTeamMembers(projectId);
      teamMembers.value = result.data || result;
      return teamMembers.value;
    } catch (err) {
      console.error('PROJECTS STORE: Error fetching team members:', err);
      error.value = err.message;
      throw err;
    } finally {
      loadingCount.value--;
    }
  };

  /**
   * Add a team member
   * @param {number|string} projectId - Project ID
   * @param {Object} memberData - Team member data
   * @returns {Promise<Object>} Added team member
   */
  const addTeamMember = async (projectId, memberData) => {
    loadingCount.value++;
    error.value = null;

    try {
      const newMember = await projectService.addTeamMember(projectId, memberData);
      teamMembers.value.push(newMember);
      return newMember;
    } catch (err) {
      console.error('PROJECTS STORE: Error adding team member:', err);
      error.value = err.message;
      throw err;
    } finally {
      loadingCount.value--;
    }
  };

  /**
   * Update a team member
   * @param {number|string} projectId - Project ID
   * @param {number|string} memberId - Team member ID
   * @param {Object} memberData - Updated member data
   * @returns {Promise<Object>} Updated team member
   */
  const updateTeamMember = async (projectId, memberId, memberData) => {
    loadingCount.value++;
    error.value = null;

    try {
      const updatedMember = await projectService.updateTeamMember(projectId, memberId, memberData);

      const index = teamMembers.value.findIndex((m) => m.id === parseInt(memberId));
      if (index !== -1) {
        teamMembers.value[index] = updatedMember;
      }

      return updatedMember;
    } catch (err) {
      console.error(`PROJECTS STORE: Error updating team member ${memberId}:`, err);
      error.value = err.message;
      throw err;
    } finally {
      loadingCount.value--;
    }
  };

  /**
   * Remove a team member
   * @param {number|string} projectId - Project ID
   * @param {number|string} memberId - Team member ID
   * @returns {Promise<Object>} Removal result
   */
  const removeTeamMember = async (projectId, memberId) => {
    loadingCount.value++;
    error.value = null;

    try {
      const result = await projectService.removeTeamMember(projectId, memberId);
      teamMembers.value = teamMembers.value.filter((m) => m.id !== parseInt(memberId));
      return result;
    } catch (err) {
      console.error(`PROJECTS STORE: Error removing team member ${memberId}:`, err);
      error.value = err.message;
      throw err;
    } finally {
      loadingCount.value--;
    }
  };

  // ==================== DOCUMENT ACTIONS ====================

  /**
   * Fetch documents for a project
   * @param {number|string} projectId - Project ID
   * @returns {Promise<Array>} Fetched documents
   */
  const fetchDocuments = async (projectId) => {
    loadingCount.value++;
    error.value = null;

    try {
      const result = await projectService.getDocuments(projectId);
      documents.value = result.data || result;
      return documents.value;
    } catch (err) {
      console.error('PROJECTS STORE: Error fetching documents:', err);
      error.value = err.message;
      throw err;
    } finally {
      loadingCount.value--;
    }
  };

  /**
   * Upload a document
   * @param {number|string} projectId - Project ID
   * @param {FormData} formData - Form data with file
   * @returns {Promise<Object>} Uploaded document
   */
  const uploadDocument = async (projectId, formData) => {
    loadingCount.value++;
    error.value = null;

    try {
      const newDocument = await projectService.uploadDocument(projectId, formData);
      documents.value.push(newDocument);
      return newDocument;
    } catch (err) {
      console.error('PROJECTS STORE: Error uploading document:', err);
      error.value = err.message;
      throw err;
    } finally {
      loadingCount.value--;
    }
  };

  /**
   * Update a document
   * @param {number|string} projectId - Project ID
   * @param {number|string} docId - Document ID
   * @param {Object} docData - Updated document data
   * @returns {Promise<Object>} Updated document
   */
  const updateDocument = async (projectId, docId, docData) => {
    loadingCount.value++;
    error.value = null;

    try {
      const updatedDocument = await projectService.updateDocument(projectId, docId, docData);

      const index = documents.value.findIndex((d) => d.id === parseInt(docId));
      if (index !== -1) {
        documents.value[index] = updatedDocument;
      }

      return updatedDocument;
    } catch (err) {
      console.error(`PROJECTS STORE: Error updating document ${docId}:`, err);
      error.value = err.message;
      throw err;
    } finally {
      loadingCount.value--;
    }
  };

  /**
   * Delete a document
   * @param {number|string} projectId - Project ID
   * @param {number|string} docId - Document ID
   * @returns {Promise<Object>} Deletion result
   */
  const deleteDocument = async (projectId, docId) => {
    loadingCount.value++;
    error.value = null;

    try {
      const result = await projectService.deleteDocument(projectId, docId);
      documents.value = documents.value.filter((d) => d.id !== parseInt(docId));
      return result;
    } catch (err) {
      console.error(`PROJECTS STORE: Error deleting document ${docId}:`, err);
      error.value = err.message;
      throw err;
    } finally {
      loadingCount.value--;
    }
  };

  /**
   * Get document download URL
   * @param {number|string} projectId - Project ID
   * @param {number|string} docId - Document ID
   * @returns {string} Download URL
   */
  const getDocumentDownloadUrl = (projectId, docId) => {
    return projectService.getDocumentDownloadUrl(projectId, docId);
  };

  // ==================== MILESTONE ACTIONS ====================

  /**
   * Fetch milestones for a project
   * @param {number|string} projectId - Project ID
   * @returns {Promise<Array>} Fetched milestones
   */
  const fetchMilestones = async (projectId) => {
    loadingCount.value++;
    error.value = null;

    try {
      const result = await projectService.getMilestones(projectId);
      milestones.value = result.data || result;
      return milestones.value;
    } catch (err) {
      console.error('PROJECTS STORE: Error fetching milestones:', err);
      error.value = err.message;
      throw err;
    } finally {
      loadingCount.value--;
    }
  };

  /**
   * Create a new milestone
   * @param {number|string} projectId - Project ID
   * @param {Object} milestoneData - Milestone data
   * @returns {Promise<Object>} Created milestone
   */
  const createMilestone = async (projectId, milestoneData) => {
    loadingCount.value++;
    error.value = null;

    try {
      const newMilestone = await projectService.createMilestone(projectId, milestoneData);
      milestones.value.push(newMilestone);
      return newMilestone;
    } catch (err) {
      console.error('PROJECTS STORE: Error creating milestone:', err);
      error.value = err.message;
      throw err;
    } finally {
      loadingCount.value--;
    }
  };

  /**
   * Update a milestone
   * @param {number|string} projectId - Project ID
   * @param {number|string} milestoneId - Milestone ID
   * @param {Object} milestoneData - Updated milestone data
   * @returns {Promise<Object>} Updated milestone
   */
  const updateMilestone = async (projectId, milestoneId, milestoneData) => {
    loadingCount.value++;
    error.value = null;

    try {
      const updatedMilestone = await projectService.updateMilestone(projectId, milestoneId, milestoneData);

      const index = milestones.value.findIndex((m) => m.id === parseInt(milestoneId));
      if (index !== -1) {
        milestones.value[index] = updatedMilestone;
      }

      return updatedMilestone;
    } catch (err) {
      console.error(`PROJECTS STORE: Error updating milestone ${milestoneId}:`, err);
      error.value = err.message;
      throw err;
    } finally {
      loadingCount.value--;
    }
  };

  /**
   * Delete a milestone
   * @param {number|string} projectId - Project ID
   * @param {number|string} milestoneId - Milestone ID
   * @returns {Promise<Object>} Deletion result
   */
  const deleteMilestone = async (projectId, milestoneId) => {
    loadingCount.value++;
    error.value = null;

    try {
      const result = await projectService.deleteMilestone(projectId, milestoneId);
      milestones.value = milestones.value.filter((m) => m.id !== parseInt(milestoneId));
      return result;
    } catch (err) {
      console.error(`PROJECTS STORE: Error deleting milestone ${milestoneId}:`, err);
      error.value = err.message;
      throw err;
    } finally {
      loadingCount.value--;
    }
  };

  /**
   * Mark a milestone as complete
   * @param {number|string} projectId - Project ID
   * @param {number|string} milestoneId - Milestone ID
   * @returns {Promise<Object>} Updated milestone
   */
  const completeMilestone = async (projectId, milestoneId) => {
    loadingCount.value++;
    error.value = null;

    try {
      const updatedMilestone = await projectService.completeMilestone(projectId, milestoneId);

      const index = milestones.value.findIndex((m) => m.id === parseInt(milestoneId));
      if (index !== -1) {
        milestones.value[index] = updatedMilestone;
      }

      return updatedMilestone;
    } catch (err) {
      console.error(`PROJECTS STORE: Error completing milestone ${milestoneId}:`, err);
      error.value = err.message;
      throw err;
    } finally {
      loadingCount.value--;
    }
  };

  // ==================== FILTER ACTIONS ====================

  /**
   * Set the filter status
   * @param {string} status - Status filter value
   */
  const setFilterStatus = (status) => {
    filterStatus.value = status;
  };

  /**
   * Set the filter health
   * @param {string} health - Health filter value
   */
  const setFilterHealth = (health) => {
    filterHealth.value = health;
  };

  /**
   * Set the filter type
   * @param {string} type - Type filter value
   */
  const setFilterType = (type) => {
    filterType.value = type;
  };

  /**
   * Set the search query
   * @param {string} query - Search query
   */
  const setSearchQuery = (query) => {
    searchQuery.value = query;
  };

  /**
   * Toggle archived projects visibility
   * @param {boolean} show - Whether to show archived projects
   */
  const toggleArchived = (show) => {
    showArchived.value = show;
  };

  /**
   * Reset all filters
   */
  const resetFilters = () => {
    filterStatus.value = 'all';
    filterHealth.value = 'all';
    filterType.value = 'all';
    searchQuery.value = '';
    showArchived.value = false;
  };

  return {
    // State
    projects,
    currentProject,
    tasks,
    teamMembers,
    documents,
    milestones,
    isLoading,
    error,
    filterStatus,
    filterHealth,
    filterType,
    searchQuery,
    showArchived,
    pagination,
    statusColors,
    healthColors,
    priorityColors,

    // Getters
    filteredProjects,
    statusOptions,
    healthOptions,
    typeOptions,
    projectStats,
    getProjectById,

    // Project actions
    fetchProjects,
    fetchProject,
    createProject,
    updateProject,
    archiveProject,
    restoreProject,
    convertTenderToProject,

    // Task actions
    fetchTasks,
    createTask,
    updateTask,
    deleteTask,
    completeTask,

    // Team member actions
    fetchTeamMembers,
    addTeamMember,
    updateTeamMember,
    removeTeamMember,

    // Document actions
    fetchDocuments,
    uploadDocument,
    updateDocument,
    deleteDocument,
    getDocumentDownloadUrl,

    // Milestone actions
    fetchMilestones,
    createMilestone,
    updateMilestone,
    deleteMilestone,
    completeMilestone,

    // Filter actions
    setFilterStatus,
    setFilterHealth,
    setFilterType,
    setSearchQuery,
    toggleArchived,
    resetFilters,
  };
});
